// Story 11.4 — Rollback dialog: lista los últimos N deployments y permite
// elegir target. Default: rollback to previous (anterior al active).

import { useQuery } from '@tanstack/react-query';
import { Loader2, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useRollbackBot } from '@/hooks/mutations/use-rollback-bot';
import { useDeployments } from '@/hooks/queries/use-deployments';
import { type BotRowSummary, type DeploymentRow, deploymentsApi } from '@/lib/api-client';
import { diffConfig, isEmpty as isDiffEmpty } from '@/lib/config-diff';
import { clientLog, type LogEntry } from '@/lib/event-to-log';
import { formatRelative } from '@/lib/relative-time';
import { cn } from '@/lib/utils';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  onClose: () => void;
  bot: BotRowSummary | undefined;
  pushLocalLog?: (entry: LogEntry) => void;
}

export function RollbackDialog({ open, onClose, bot, pushLocalLog }: Props): React.ReactElement {
  const botId = bot?.id ?? -1;
  const list = useDeployments(botId, { limit: 10 }, open);
  const mutation = useRollbackBot(botId);
  const [selected, setSelected] = useState<DeploymentRow | null>(null);

  // Reset selection cuando el dialog se abre.
  useEffect(() => {
    if (open) setSelected(null);
  }, [open]);

  const candidates = (list.data ?? []).filter((d) => d.status !== 'active');
  const empty = !list.isLoading && candidates.length === 0;

  // Cargamos el detail (config + newConfig) del deployment seleccionado
  // SOLO cuando hay selección — sin esto el dialog haría N requests por
  // candidato apenas el user abre el modal.
  const { data: selectedDetail } = useQuery({
    queryKey: ['bot', botId, 'deployments', selected?.id, 'detail'],
    queryFn: () => deploymentsApi.getDetail(botId, selected?.id as number),
    enabled: open && botId > 0 && selected != null,
    staleTime: 60_000,
  });
  const diff = useMemo(() => {
    if (!bot || !selectedDetail) return null;
    // Diff: estado tras rollback (el `newConfig` del target) vs estado actual
    // del bot. Mostramos al user qué va a CAMBIAR si confirma.
    return diffConfig(bot.config ?? {}, selectedDetail.newConfig ?? {});
  }, [bot, selectedDetail]);

  function handleConfirm(): void {
    if (!bot) return;
    const targetVersion = selected?.version;
    pushLocalLog?.(
      clientLog('warn', `Rolling back ${bot.name}${targetVersion ? ` → ${targetVersion}` : ''}…`, {
        targetVersion,
      }),
    );
    mutation.mutate(
      { targetVersion },
      {
        onSuccess: ({ toDeployment }) => {
          pushLocalLog?.(clientLog('info', `Rollback ${toDeployment.version} activo.`, {}));
          onClose();
        },
        onError: (err) => {
          pushLocalLog?.(clientLog('error', `Rollback falló: ${(err as Error).message}`, {}));
        },
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Rollback"
      description={
        bot
          ? `Restaurar una versión anterior de "${bot.name}". El backend marca el actual como rolled-back.`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={mutation.isPending || empty || !bot}
            variant="default"
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Undo2 className="h-4 w-4" />
            )}
            {selected ? `Rollback a ${selected.version}` : 'Rollback al anterior'}
          </Button>
        </>
      }
    >
      {list.isLoading && <p className="text-sm text-muted-foreground">Cargando historial…</p>}
      {list.isError && <p className="text-sm text-destructive">No se pudo cargar el historial.</p>}
      {empty && (
        <p className="text-sm text-muted-foreground">
          Este bot no tiene historial de deployments. Haz un Deploy primero para crear el primer
          snapshot.
        </p>
      )}
      {!empty && candidates.length > 0 && selected && diff && (
        <div className="mb-3 rounded-md border border-border bg-muted/40 p-3 text-xs">
          <div className="mb-1 font-semibold">
            Cambios al hacer rollback
            <span className="ml-1 font-mono text-[10px] text-muted-foreground">
              (current → {selected.version})
            </span>
          </div>
          {isDiffEmpty(diff) ? (
            <p className="text-muted-foreground">
              El estado actual ya coincide con esa versión. El rollback no va a cambiar nada en el
              config — sólo registrará un nuevo snapshot.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-3">
                {diff.added.length > 0 && (
                  <span className="text-success">+{diff.added.length} se restauran</span>
                )}
                {diff.changed.length > 0 && (
                  <span className="text-amber-500">~{diff.changed.length} se modifican</span>
                )}
                {diff.removed.length > 0 && (
                  <span className="text-destructive">-{diff.removed.length} se eliminan</span>
                )}
              </div>
              <ul className="mt-2 max-h-32 space-y-0.5 overflow-y-auto font-mono text-[10px]">
                {diff.added.slice(0, 5).map((p) => (
                  <li key={`a-${p}`} className="text-success">
                    + {p}
                  </li>
                ))}
                {diff.changed.slice(0, 5).map((p) => (
                  <li key={`c-${p}`} className="text-amber-500">
                    ~ {p}
                  </li>
                ))}
                {diff.removed.slice(0, 5).map((p) => (
                  <li key={`r-${p}`} className="text-destructive">
                    - {p}
                  </li>
                ))}
              </ul>
              {diff.added.length + diff.changed.length + diff.removed.length > 15 && (
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Mostrando primeros 5 de cada tipo.
                </p>
              )}
            </>
          )}
        </div>
      )}
      {!empty && candidates.length > 0 && (
        <ul className="divide-y divide-border" aria-label="Versiones disponibles">
          {candidates.map((d) => {
            const isSelected = selected?.id === d.id;
            return (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => setSelected(d)}
                  className={cn(
                    'flex w-full items-start justify-between gap-3 px-2 py-2 text-left text-sm hover:bg-accent/30',
                    isSelected && 'bg-accent/40',
                  )}
                  aria-pressed={isSelected}
                >
                  <div className="min-w-0">
                    <p className="font-mono text-xs font-semibold">{d.version}</p>
                    {d.notes && <p className="truncate text-xs text-muted-foreground">{d.notes}</p>}
                  </div>
                  <div className="shrink-0 text-right text-[10px] text-muted-foreground">
                    <span className="block uppercase">{d.status}</span>
                    <span>{formatRelative(d.createdAt)}</span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
